const { Core, Events } = Global;

const ROOT_HIDE_ATTRIBUTE = 'data-hide-green-video-gallery';
const ROOT_POSITION_ATTRIBUTE = 'data-green-video-gallery-position';
const GREEN_VIDEO_ITEM_SELECTOR = '[data-green-video-gallery-item]';
const GREEN_VIDEO_ID_ATTRIBUTE = 'data-green-video-id';
const GREEN_VIDEO_ORDER_ATTRIBUTE = 'data-green-video-order';
const NATIVE_INDEX_ATTRIBUTE = 'data-gallery-native-index';
const DEFAULT_INDEX_ATTRIBUTE = 'data-gallery-default-index';
// Keep the original gallery as the default; the public API can opt into the enhanced gallery.
let productGalleryEnhancedMode = false;

function clamp(value, min, max) {
    return Math.max(min, Math.min(value, max));
}

function assertPosition(position) {
    const requestedPosition = Number(position);
    if(!Number.isInteger(requestedPosition) || requestedPosition < 1) {
        throw new RangeError('Green video gallery position must be a positive integer.');
    }
    return requestedPosition;
}

customElements.define('product-gallery-manager', class extends Core {
    render() {
        this._handleGalleryUpdate = this._handleGalleryUpdate.bind(this);
        this._syncGreenVideoState = this._syncGreenVideoState.bind(this);
        this.hiddenGreenVideoIds = new Set();
        this.greenVideoPositionOverrides = new Map();
        this.positionOverrideActive = false;
        this.enhancedMode = productGalleryEnhancedMode;

        this._captureGalleryState();
        this.addEventListener('update', this._handleGalleryUpdate);
        this.rootObserver = new MutationObserver(this._syncGreenVideoState);
        this.rootObserver.observe(document.documentElement, {
            attributes: true,
            attributeFilter: [ROOT_HIDE_ATTRIBUTE, ROOT_POSITION_ATTRIBUTE]
        });

        this._syncGreenVideoState();
        this._applyEnhancedMode();
    }

    disconnectedCallback() {
        this.removeEventListener('update', this._handleGalleryUpdate);
        this.rootObserver?.disconnect();
        clearTimeout(this.greenVideoRefreshTimer);
        super.disconnectedCallback();
    }

    _handleGalleryUpdate() {
        this._captureGalleryState();
        this._syncGreenVideoState();
        this._applyEnhancedMode();
    }

    setEnhancedMode(enabled) {
        this.enhancedMode = Boolean(enabled);
        this._applyEnhancedMode();
        return this.enhancedMode;
    }

    _applyEnhancedMode() {
        if(this.enhancedMode) {
            this.setAttribute('data-gallery-enhanced', 'true');
        } else {
            this.removeAttribute('data-gallery-enhanced');
        }

        const applyToThumbnails = () => {
            if(!this.isConnected) {
                return;
            }
            this.querySelectorAll('product-gallery-thumbnails').forEach((thumbnails) => {
                thumbnails.setEnhancedMode?.(this.enhancedMode);
            });
        };

        if(customElements.get('product-gallery-thumbnails')) {
            applyToThumbnails();
        } else {
            customElements.whenDefined('product-gallery-thumbnails').then(applyToThumbnails);
        }
    }

    _captureGalleryState() {
        const containers = Array.from(new Set([
            ...this.querySelectorAll('product-gallery-slider .blaze-track'),
            ...this.querySelectorAll('product-gallery-scrollable'),
            ...this.querySelectorAll('product-gallery-thumbnails'),
            ...this.querySelectorAll('product-gallery-expand .blaze-track')
        ])).filter((container) => {
            return Array.from(container.children).some((item) => {
                return item.hasAttribute(DEFAULT_INDEX_ATTRIBUTE);
            });
        });

        this.galleryContainerStates = containers.map((container) => {
            const items = Array.from(container.children)
                .filter((item) => item.hasAttribute(DEFAULT_INDEX_ATTRIBUTE));
            return {
                container,
                itemsByKey: new Map(items.map((item) => [this._getGalleryItemKey(item), item]))
            };
        });

        const representativeState = this.galleryContainerStates.find(({ itemsByKey }) => {
            return Array.from(itemsByKey.keys()).some((key) => key.startsWith('video:'));
        });
        const representativeItems = representativeState
            ? Array.from(representativeState.itemsByKey.values())
            : [];

        this.defaultGalleryOrder = representativeItems
            .sort((a, b) => {
                return +a.getAttribute(DEFAULT_INDEX_ATTRIBUTE)
                    - +b.getAttribute(DEFAULT_INDEX_ATTRIBUTE);
            })
            .map((item) => this._getGalleryItemKey(item));

        this.greenVideoIds = Array.from(new Map(
            this._getGreenVideoItems()
                .sort((a, b) => {
                    return +a.getAttribute(GREEN_VIDEO_ORDER_ATTRIBUTE)
                        - +b.getAttribute(GREEN_VIDEO_ORDER_ATTRIBUTE);
                })
                .map((item) => [item.getAttribute(GREEN_VIDEO_ID_ATTRIBUTE), true])
        ).keys()).filter(Boolean);

        const configuredPrimaryId = this.dataset.greenVideoPrimaryId;
        this.primaryGreenVideoId = this.greenVideoIds.includes(configuredPrimaryId)
            ? configuredPrimaryId
            : this.greenVideoIds[0] || null;
    }

    _syncGreenVideoState() {
        if(!this.primaryGreenVideoId) {
            return;
        }

        const position = document.documentElement.getAttribute(ROOT_POSITION_ATTRIBUTE);
        if(position !== null) {
            const requestedPosition = Number(position);
            if(Number.isInteger(requestedPosition) && requestedPosition >= 1) {
                this.greenVideoPositionOverrides.set(this.primaryGreenVideoId, requestedPosition);
            } else {
                this.greenVideoPositionOverrides.delete(this.primaryGreenVideoId);
            }
            this.positionOverrideActive = true;
        } else if(this.positionOverrideActive) {
            this.greenVideoPositionOverrides.delete(this.primaryGreenVideoId);
            this.positionOverrideActive = false;
        }

        this._applyGalleryState();
    }

    hideGreenVideo(videoId = this.primaryGreenVideoId) {
        if(!this._hasGreenVideo(videoId)) {
            return false;
        }
        this.hiddenGreenVideoIds.add(videoId);
        this._applyGalleryState();
        return true;
    }

    showGreenVideo(videoId = this.primaryGreenVideoId) {
        if(!this._hasGreenVideo(videoId)) {
            return false;
        }
        this.hiddenGreenVideoIds.delete(videoId);
        this._applyGalleryState();
        return true;
    }

    hideAllGreenVideos() {
        this.greenVideoIds.forEach((videoId) => this.hiddenGreenVideoIds.add(videoId));
        this._applyGalleryState();
    }

    showAllGreenVideos() {
        this.hiddenGreenVideoIds.clear();
        this._applyGalleryState();
    }

    moveGreenVideo(videoId, position) {
        if(position === undefined) {
            position = videoId;
            videoId = this.primaryGreenVideoId;
        }
        if(!this._hasGreenVideo(videoId)) {
            return null;
        }

        this.greenVideoPositionOverrides.set(videoId, assertPosition(position));
        return this._applyGalleryState();
    }

    moveGreenVideos(moves) {
        const validMoves = this._normalizeMoves(moves)
            .filter(({ videoId }) => this._hasGreenVideo(videoId))
            .map(({ videoId, position }) => ({
                videoId,
                position: assertPosition(position)
            }));

        validMoves.forEach(({ videoId, position }) => {
            this.greenVideoPositionOverrides.set(videoId, position);
        });
        return this._applyGalleryState();
    }

    resetGreenVideo(videoId = this.primaryGreenVideoId) {
        if(!this._hasGreenVideo(videoId)) {
            return false;
        }
        this.greenVideoPositionOverrides.delete(videoId);
        this._applyGalleryState();
        return true;
    }

    resetAllGreenVideos() {
        this.greenVideoPositionOverrides.clear();
        this.positionOverrideActive = false;
        delete this.dataset.greenVideoPositionApplied;
        this._applyGalleryState();
    }

    removeGreenVideo(videoId = this.primaryGreenVideoId) {
        return this.hideGreenVideo(videoId);
    }

    getGreenVideoIds() {
        return [...this.greenVideoIds];
    }

    _applyGalleryState() {
        if(this.applyingGreenVideoState || !this.defaultGalleryOrder.length) {
            return null;
        }

        this.applyingGreenVideoState = true;
        const sliderStates = this._getSliderElements();
        const galleryOrder = this._buildGalleryOrder();
        const visibleOrder = galleryOrder.filter((key) => !this._isGalleryItemHidden(key));
        let galleryStructureChanged = false;

        try {
            this.galleryContainerStates.forEach(({ container, itemsByKey }) => {
                itemsByKey.forEach((item, key) => {
                    if(this._isGalleryItemHidden(key) && item.parentElement === container) {
                        item.querySelectorAll('video-player').forEach((video) => video.pause?.());
                        item.remove();
                        galleryStructureChanged = true;
                    }
                });

                visibleOrder.forEach((key, targetIndex) => {
                    const item = itemsByKey.get(key);
                    if(!item) {
                        return;
                    }

                    const currentItems = Array.from(container.children)
                        .filter((child) => child.hasAttribute(DEFAULT_INDEX_ATTRIBUTE));
                    if(currentItems[targetIndex] === item) {
                        return;
                    }

                    const referenceItem = currentItems[targetIndex];
                    if(referenceItem) {
                        container.insertBefore(item, referenceItem);
                    } else if(currentItems.length) {
                        currentItems.at(-1).after(item);
                    } else {
                        container.append(item);
                    }
                    galleryStructureChanged = true;
                });
            });

            this._updateGalleryIndexes(visibleOrder);
            this._refreshGalleryCollections();
        } finally {
            this.applyingGreenVideoState = false;
        }

        const primaryKey = this._getVideoKey(this.primaryGreenVideoId);
        const primaryIndex = visibleOrder.indexOf(primaryKey);
        const currentGalleryIndex = this._refreshSliders(
            sliderStates,
            primaryIndex >= 0 ? primaryIndex : 0
        );

        this._updateLegacyAttributes(primaryIndex);
        this.pub(Events.PRODUCT_GALLERY_SLIDE_CHANGE, currentGalleryIndex);
        if(galleryStructureChanged) {
            this._scheduleGreenVideoRefresh();
        }
        return primaryIndex;
    }

    _scheduleGreenVideoRefresh() {
        clearTimeout(this.greenVideoRefreshTimer);
        this.greenVideoRefreshTimer = setTimeout(() => {
            this.querySelectorAll('video-player[green-video-mode][inited]')
                .forEach((video) => video.reinitializeGreenVideo?.());
        });
    }

    _buildGalleryOrder() {
        const overriddenVideoIds = this.greenVideoIds.filter((videoId) => {
            return this.greenVideoPositionOverrides.has(videoId);
        });
        if(!overriddenVideoIds.length) {
            return [...this.defaultGalleryOrder];
        }

        const overriddenKeys = new Set(overriddenVideoIds.map((videoId) => this._getVideoKey(videoId)));
        const remainingItems = this.defaultGalleryOrder.filter((key) => !overriddenKeys.has(key));
        const galleryOrder = [];
        const renderedVideoIds = new Set();
        const totalItemCount = this.defaultGalleryOrder.length;

        for(let galleryPosition = 1; galleryPosition <= totalItemCount; galleryPosition += 1) {
            const videoId = overriddenVideoIds.find((candidateId) => {
                if(renderedVideoIds.has(candidateId)) {
                    return false;
                }
                const requestedPosition = clamp(
                    this.greenVideoPositionOverrides.get(candidateId),
                    1,
                    totalItemCount
                );
                return requestedPosition <= galleryPosition;
            });

            if(videoId) {
                galleryOrder.push(this._getVideoKey(videoId));
                renderedVideoIds.add(videoId);
            } else if(remainingItems.length) {
                galleryOrder.push(remainingItems.shift());
            }
        }

        overriddenVideoIds.forEach((videoId) => {
            if(!renderedVideoIds.has(videoId)) {
                galleryOrder.push(this._getVideoKey(videoId));
            }
        });
        return galleryOrder;
    }

    _updateGalleryIndexes(visibleOrder) {
        const indexByKey = new Map(visibleOrder.map((key, index) => [key, index]));

        this.galleryContainerStates.forEach(({ itemsByKey }) => {
            itemsByKey.forEach((item, key) => {
                const galleryIndex = indexByKey.has(key) ? indexByKey.get(key) : -1;
                const indexedElements = [
                    ...(item.hasAttribute('data-index') ? [item] : []),
                    ...item.querySelectorAll('[data-index]')
                ];
                indexedElements.forEach((element) => {
                    element.dataset.index = galleryIndex;
                });
                item.querySelectorAll('input[data-element="input"]').forEach((input) => {
                    input.value = galleryIndex;
                });
            });
        });
    }

    _updateLegacyAttributes(primaryIndex) {
        const primaryHidden = primaryIndex < 0;
        const indexedElements = [
            this,
            ...this.querySelectorAll('product-gallery-slider, product-gallery-scrollable')
        ];
        indexedElements.forEach((element) => {
            if(primaryHidden) {
                element.removeAttribute('green-video-index');
            } else {
                element.setAttribute('green-video-index', primaryIndex);
            }
        });

        if(primaryHidden) {
            this.dataset.greenVideoRemoved = 'true';
        } else {
            delete this.dataset.greenVideoRemoved;
        }

        if(document.documentElement.hasAttribute(ROOT_POSITION_ATTRIBUTE)) {
            this.dataset.greenVideoPositionApplied = primaryIndex + 1;
        } else {
            delete this.dataset.greenVideoPositionApplied;
        }
    }

    _getSliderElements() {
        const states = new Map();
        this.querySelectorAll('slideshow-blaze').forEach((slider) => {
            const slides = Array.from(slider.querySelector('.blaze-track')?.children || []);
            const currentIndex = slider.slider?.stateIndex
                ?? +(slider.getAttribute('initial-index') || 0);
            const currentSlide = slides.find((slide) => slide.classList.contains('blaze-active'))
                || slides[currentIndex]
                || slides[0]
                || null;
            states.set(slider, { currentSlide, currentIndex });
        });
        return states;
    }

    _refreshGalleryCollections() {
        this.querySelectorAll('product-gallery-thumbnails').forEach((thumbnails) => {
            thumbnails.refreshInputs?.();
        });
        this.querySelectorAll('product-gallery-scrollable').forEach((gallery) => {
            gallery.refreshMedia?.();
        });
    }

    _refreshSliders(sliderStates, fallbackIndex) {
        let currentIndex;
        let firstSliderIndex;

        sliderStates.forEach(({ currentSlide, currentIndex: previousIndex }, slider) => {
            const slides = Array.from(slider.querySelector('.blaze-track')?.children || []);
            const slideIndex = slides.indexOf(currentSlide);
            const safeIndex = slideIndex >= 0
                ? slideIndex
                : clamp(previousIndex ?? fallbackIndex, 0, Math.max(slides.length - 1, 0));

            if(firstSliderIndex === undefined) {
                firstSliderIndex = safeIndex;
            }
            if(currentIndex === undefined && slider.closest('product-gallery-slider')) {
                currentIndex = safeIndex;
            }

            if(typeof slider.refreshSlides === 'function') {
                slider.refreshSlides(safeIndex);
            } else {
                slider.setAttribute('initial-index', safeIndex);
            }
        });

        return currentIndex ?? firstSliderIndex ?? fallbackIndex;
    }

    _getGreenVideoItems() {
        return Array.from(this.querySelectorAll(GREEN_VIDEO_ITEM_SELECTOR));
    }

    _getGalleryItemKey(item) {
        const videoId = item.getAttribute(GREEN_VIDEO_ID_ATTRIBUTE);
        if(videoId) {
            return this._getVideoKey(videoId);
        }
        return `native:${item.getAttribute(NATIVE_INDEX_ATTRIBUTE)}`;
    }

    _getVideoKey(videoId) {
        return `video:${videoId}`;
    }

    _hasGreenVideo(videoId) {
        return typeof videoId === 'string' && this.greenVideoIds.includes(videoId);
    }

    _isGalleryItemHidden(key) {
        if(!key.startsWith('video:')) {
            return false;
        }
        const videoId = key.slice('video:'.length);
        return this.hiddenGreenVideoIds.has(videoId)
            || (
                videoId === this.primaryGreenVideoId
                && document.documentElement.hasAttribute(ROOT_HIDE_ATTRIBUTE)
            );
    }

    _normalizeMoves(moves) {
        if(Array.isArray(moves)) {
            return moves.map((move) => {
                if(Array.isArray(move)) {
                    return { videoId: move[0], position: move[1] };
                }
                return { videoId: move?.id ?? move?.videoId, position: move?.position };
            });
        }
        if(moves && typeof moves === 'object') {
            return Object.entries(moves).map(([videoId, position]) => ({ videoId, position }));
        }
        throw new TypeError('Green video moves must be an array or an object keyed by video ID.');
    }
});

function getGalleryManagers() {
    return Array.from(document.querySelectorAll('product-gallery-manager'));
}

function callManagers(method, ...args) {
    return getGalleryManagers().map((manager) => manager[method]?.(...args));
}

window.MSGGreenVideoGallery = {
    getVideoIds() {
        return Array.from(new Set(
            getGalleryManagers().flatMap((manager) => manager.getGreenVideoIds?.() || [])
        ));
    },
    hide(videoId) {
        return callManagers('hideGreenVideo', videoId);
    },
    show(videoId) {
        if(getGalleryManagers().some((manager) => manager.primaryGreenVideoId === videoId)) {
            document.documentElement.removeAttribute(ROOT_HIDE_ATTRIBUTE);
        }
        return callManagers('showGreenVideo', videoId);
    },
    hideAll() {
        return callManagers('hideAllGreenVideos');
    },
    showAll() {
        document.documentElement.removeAttribute(ROOT_HIDE_ATTRIBUTE);
        return callManagers('showAllGreenVideos');
    },
    move(videoId, position) {
        assertPosition(position);
        return callManagers('moveGreenVideo', videoId, position);
    },
    moveMany(moves) {
        return callManagers('moveGreenVideos', moves);
    },
    reset(videoId) {
        return callManagers('resetGreenVideo', videoId);
    },
    resetAll() {
        document.documentElement.removeAttribute(ROOT_POSITION_ATTRIBUTE);
        return callManagers('resetAllGreenVideos');
    },
    remove() {
        document.documentElement.setAttribute(ROOT_HIDE_ATTRIBUTE, '');
    },
    moveTo(position) {
        document.documentElement.setAttribute(ROOT_POSITION_ATTRIBUTE, assertPosition(position));
    },
    resetPosition() {
        document.documentElement.removeAttribute(ROOT_POSITION_ATTRIBUTE);
    }
};

window.MSGProductGallery = {
    setEnhancedMode(enabled) {
        productGalleryEnhancedMode = Boolean(enabled);
        return callManagers('setEnhancedMode', productGalleryEnhancedMode);
    }
};
